import React, { useState, useMemo } from "react";
import { useCompanyManagement } from "@/hooks/useCompanyManagement";
import { Company } from "@/types/company";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, Plus, X } from "lucide-react";
import { CompanyFormDialog } from "./CompanyFormDialog";
import { Pagination } from "@/components/ui/pagination";
import { companyUILabels, commonMiscLabels } from "@/i18n";

interface CompanySelectorProps {
  selectedCompanyId?: string;
  onCompanySelect: (company: Company) => void;
}

export const CompanySelector: React.FC<CompanySelectorProps> = ({
  selectedCompanyId,
  onCompanySelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // i18n labels
  const uiLabels = companyUILabels();
  const miscLabels = commonMiscLabels();

  const {
    companies,
    isLoadingCompanies,
    companiesError,
    refetchCompanies,
    totalCount,
    totalPages,
    currentPage: serverCurrentPage,
  } = useCompanyManagement(undefined, currentPage, itemsPerPage, activeSearchQuery);

  // Companies are now filtered server-side, so we use them directly
  const filteredCompanies = companies;

  // Companies are now filtered server-side, so we use them directly
  const displayCompanies = filteredCompanies;
  const displayCurrentPage = currentPage;
  const displayTotalPages = totalPages;
  const displayTotalItems = totalCount;
  const handlePageChange = setCurrentPage;
  const handleItemsPerPageChange = setItemsPerPage;

  // Handle search
  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setCurrentPage(1);
  };

  const handleCompanySelect = (company: Company) => {
    onCompanySelect(company);
  };

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    refetchCompanies();
    // Reset to first page when new company is created
    setCurrentPage(1);
  };

  if (isLoadingCompanies) {
    return (
      <div className={`space-y-4`}>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">{uiLabels.loadingCompanies}</p>
        </div>
      </div>
    );
  }

  if (companiesError) {
    return (
      <div className={`space-y-4`}>
        <div className="text-center py-8">
          <p className="text-red-500">{miscLabels.errorDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4`}>
      {/* Description */}
      <div className="text-center">
        <p className="text-gray-600 dark:text-gray-400">
          {uiLabels.selectCompanyDescription}
        </p>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={uiLabels.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleSearch();
              }
            }}
            className="pl-10"
          />
        </div>
        <Button type="button" onClick={handleSearch} variant="outline">
          <Search className="h-4 w-4 mr-2" />
          {uiLabels.search}
        </Button>
        {activeSearchQuery && (
          <Button type="button" onClick={handleClearSearch} variant="ghost">
            <X className="h-4 w-4 mr-2" />
            {miscLabels.clear}
          </Button>
        )}
      </div>

      {/* Company List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {displayCompanies.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {activeSearchQuery
                ? uiLabels.noCompaniesSearch
                : uiLabels.noCompanies}
            </p>
          </div>
        ) : (
          displayCompanies.map((company) => (
            <Card
              key={company._id}
              className={`cursor-pointer m-1 transition-all hover:shadow-md ${
                selectedCompanyId === company._id
                  ? "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
              onClick={() => handleCompanySelect(company)}
            >
              <CardContent className="p-4">
                <div className="flex flex-row items-center">
                  <p className="font-semibold text-sm md:text-lg text-gray-900 dark:text-gray-100">
                    {company.name}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs ml-2 rounded-lg border bg-primary/10 p-1 md:text-sm">
                      {company.organisationNumber}
                    </p>
                    <p className="text-xs md:text-sm text-gray-500">
                      {company.city}
                    </p>
                  </div>
                  {selectedCompanyId === company._id && (
                    <div className="text-blue-500 pl-4 pt-1.5">
                      <svg
                        className="h-5 w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {displayTotalPages > 1 && (
        <Pagination
          currentPage={displayCurrentPage}
          totalPages={displayTotalPages}
          totalItems={displayTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[5, 10, 15, 20]}
        />
      )}

      {/* Add New Company Button */}
      <div className="text-center">
        <Button
          variant="outline"
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {uiLabels.addCompany}
        </Button>
      </div>

      {/* Create Company Dialog */}
      <CompanyFormDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
